import React from 'react';
import { formatearFecha } from '../../../shared/utils/dateUtils';
import { DEFAULT_VALUES } from '../../../core/constants';
import styles from './CopagoBadge.module.css';

/**
 * Badge de estado del copago de un turno.
 *
 * - `copago <= 0` -> indicador "sin copago" (guion).
 * - `copago > 0` y `copagoPago` verdadero -> badge "Pagado".
 * - `copago > 0` y `copagoPago` falso -> badge "Pendiente".
 * - `copagoOrigen === 'Automatico'` -> tooltip/aria-label con el numero de
 *   operacion, la fecha del pago (formateada) y el medio de pago de MercadoPago.
 * - `copagoPago` verdadero sin origen automatico -> "Pagado" sin tooltip de operacion.
 */
function CopagoBadge({ turno }) {
  const copago = turno?.copago ?? 0;

  // Requirement 3.2: sin copago cuando el monto es cero o negativo.
  if (copago <= 0) {
    return (
      <span className={styles['copago-sin-copago']} aria-label="Sin copago">
        {DEFAULT_VALUES.NO_DATA_DASH}
      </span>
    );
  }

  // Requirement 3.1: estado del badge segun copagoPago.
  const pagado = Boolean(turno?.copagoPago);
  const esAutomatico = turno?.copagoOrigen === 'Automatico';

  // Requirement 3.3 / 3.4 / 6.2: el detalle de operacion solo aplica al pago automatico.
  if (pagado && esAutomatico) {
    const nroOperacion = turno?.copagoNroOperacion || DEFAULT_VALUES.NO_DATA_DASH;
    const fechaPago = turno?.copagoFechaPago
      ? formatearFecha(turno.copagoFechaPago)
      : DEFAULT_VALUES.NO_DATA_DASH;
    const medio = turno?.copagoMedio || DEFAULT_VALUES.NO_DATA_DASH;

    const detalle = `Pago registrado por MercadoPago. Número de operación: ${nroOperacion}. Fecha del pago: ${fechaPago}. Medio de pago: ${medio}.`;

    return (
      <span
        className={`${styles['copago-badge']} ${styles['copago-pagado']}`}
        role="status"
        title={detalle}
        aria-label={detalle}
      >
        Pagado
      </span>
    );
  }

  // Requirement 3.1 / 3.4 / 6.2: pagado manual/antiguo sin tooltip de operacion.
  if (pagado) {
    return (
      <span
        className={`${styles['copago-badge']} ${styles['copago-pagado']}`}
        role="status"
        aria-label="Copago pagado"
      >
        Pagado
      </span>
    );
  }

  // Requirement 3.1: copago pendiente.
  return (
    <span
      className={`${styles['copago-badge']} ${styles['copago-pendiente']}`}
      role="status"
      aria-label="Copago pendiente"
    >
      Pendiente
    </span>
  );
}

export default CopagoBadge;
